<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAlert } from 'dashboard/composables';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import DeleteDialog from 'dashboard/components-next/pilot/pageComponents/DeleteDialog.vue';
import BulkDeleteDialog from 'dashboard/components-next/pilot/pageComponents/BulkDeleteDialog.vue';
import BulkSelectBar from 'dashboard/components-next/pilot/assistant/BulkSelectBar.vue';
import KnowledgeBaseFaqsFilter from 'dashboard/components-next/filter/KnowledgeBaseFaqsFilter.vue';
import PilotFaqsAPI from 'dashboard/api/pilot/faqs';

const props = defineProps({
  knowledgeBase: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['refresh', 'create']);
const router = useRouter();
const { t } = useI18n();

// ============================================================
// 状态配置（必须在计算属性之前定义）
// ============================================================
const STATUS_CONFIG = {
  draft: {
    bgColor: 'bg-n-slate-1 dark:bg-n-slate-2',
    textColor: 'text-n-slate-10 dark:text-n-slate-9',
    dotColor: 'bg-n-slate-5',
  },
  published: {
    bgColor: 'bg-n-green-1 dark:bg-n-green-2',
    textColor: 'text-n-green-10 dark:text-n-green-9',
    dotColor: 'bg-n-green-5',
  },
};

// 向量化状态配置
const EMBEDDING_STATUS_CONFIG = {
  embedding_pending: {
    bgColor: 'bg-n-slate-1 dark:bg-n-slate-2',
    textColor: 'text-n-slate-10 dark:text-n-slate-9',
    dotColor: 'bg-n-slate-5',
  },
  embedding_processing: {
    bgColor: 'bg-n-blue-1 dark:bg-n-blue-2',
    textColor: 'text-n-blue-10 dark:text-n-blue-9',
    dotColor: 'bg-n-blue-5',
  },
  embedding_completed: {
    bgColor: 'bg-n-green-1 dark:bg-n-green-2',
    textColor: 'text-n-green-10 dark:text-n-green-9',
    dotColor: 'bg-n-green-5',
  },
  embedding_failed: {
    bgColor: 'bg-n-ruby-1 dark:bg-n-ruby-2',
    textColor: 'text-n-ruby-10 dark:text-n-ruby-9',
    dotColor: 'bg-n-ruby-5',
  },
};

// ============================================================
// 数据状态
// ============================================================
const isLoading = ref(false);
const faqs = ref([]);
const currentPage = ref(1);
const faqsMeta = ref({
  totalCount: 0,
  currentPage: 1,
  totalPages: 1,
  perPage: 25,
});

// 搜索状态
const searchValue = ref('');

// 筛选、排序状态
const sortBy = ref('created_at');
const sortOrder = ref('-');
const isFilterOpen = ref(false);
const appliedFilters = ref([]);
const filterQuery = ref([]);

// 排序弹窗
const isSortMenuOpen = ref(false);

// 批量选择状态
const bulkSelectedIds = ref(new Set());
const hoveredRow = ref(null);

// 删除状态
const deleteFaqDialog = ref(null);
const selectedFaq = ref(null);

// 重试状态
const isRetrying = ref({});

// 计算属性
const isSearching = computed(() => searchValue.value.trim() !== '');
const isFiltering = computed(() => appliedFilters.value.length > 0);
const hasFaqs = computed(() => faqs.value && faqs.value.length > 0);
const showEmptyState = computed(
  () => !isSearching.value && !isFiltering.value && !hasFaqs.value
);
const showNoResults = computed(
  () => (isSearching.value || isFiltering.value) && !hasFaqs.value
);

// 排序选项
const sortMenus = computed(() => [
  { label: t('PILOT.KNOWLEDGE_BASES.FAQS.SORT_QUESTION'), value: 'question' },
  {
    label: t('PILOT.KNOWLEDGE_BASES.FAQS.SORT_CREATED_AT'),
    value: 'created_at',
  },
  {
    label: t('PILOT.KNOWLEDGE_BASES.FAQS.SORT_UPDATED_AT'),
    value: 'updated_at',
  },
]);

const orderingMenus = computed(() => [
  { label: t('PILOT.KNOWLEDGE_BASES.SORT.ASCENDING'), value: '' },
  { label: t('PILOT.KNOWLEDGE_BASES.SORT.DESCENDING'), value: '-' },
]);

const activeSortLabel = computed(() => {
  const selectedMenu = sortMenus.value.find(
    menu => menu.value === sortBy.value
  );
  return selectedMenu?.label || t('PILOT.KNOWLEDGE_BASES.FAQS.SORT_CREATED_AT');
});

const activeOrderingLabel = computed(() => {
  const selectedMenu = orderingMenus.value.find(
    menu => menu.value === sortOrder.value
  );
  return selectedMenu?.label || t('PILOT.KNOWLEDGE_BASES.SORT.DESCENDING');
});

// 带样式的 FAQ 列表
const faqsWithStyles = computed(() => {
  return faqs.value.map(faq => {
    const statusConfig = STATUS_CONFIG[faq.status] || STATUS_CONFIG.draft;
    const embeddingConfig =
      EMBEDDING_STATUS_CONFIG[faq.embedding_status] ||
      EMBEDDING_STATUS_CONFIG.embedding_pending;
    return {
      ...faq,
      statusClass: `${statusConfig.bgColor} ${statusConfig.textColor}`,
      statusDotClass: statusConfig.dotColor,
      embeddingStatusClass: `${embeddingConfig.bgColor} ${embeddingConfig.textColor}`,
      embeddingStatusDotClass: embeddingConfig.dotColor,
    };
  });
});

// 图片路径
import faqThumbnail from 'assets/images/dashboard/pilot/faqs-light.svg';
import faqThumbnailDark from 'assets/images/dashboard/pilot/faqs-dark.svg';

// 示例 FAQ 数据
const exampleFaqs = [
  { question: '什么是 AI？', answer: 'AI 是人工智能...' },
  { question: '如何使用助手？', answer: '在对话页面选择...' },
  { question: '支持哪些功能？', answer: '问答、文档检索...' },
];

// ============================================================
// 获取 FAQ 列表
// ============================================================
const fetchFaqs = async (page = 1) => {
  isLoading.value = true;
  try {
    const params = { page };

    // 搜索
    if (searchValue.value) {
      params.search = searchValue.value;
    }

    // 排序
    if (sortBy.value) {
      const sortPrefix = sortOrder.value === '-' ? '-' : '';
      params.sort = `${sortPrefix}${sortBy.value}`;
    }

    let response;

    // 如果有筛选条件，使用 filter API
    if (isFiltering.value) {
      const snakeCaseFilters = useSnakeCase(
        JSON.parse(JSON.stringify(appliedFilters.value))
      );
      const filterPayload = filterQueryGenerator(snakeCaseFilters);

      const { data } = await PilotFaqsAPI.filter(
        page,
        params.sort,
        { knowledge_base_id: props.knowledgeBase.id, ...filterPayload },
        searchValue.value
      );

      response = data;
    } else {
      const { data } = await PilotFaqsAPI.get({
        ...params,
        knowledgeBaseId: props.knowledgeBase.id,
      });
      response = data;
    }

    faqs.value = response.payload?.faqs || [];

    if (response.meta) {
      faqsMeta.value = {
        totalCount: response.meta.total_count || faqs.value.length,
        currentPage: response.meta.current_page || 1,
        totalPages: response.meta.total_pages || 1,
        perPage: response.meta.per_page || 25,
      };
      currentPage.value = response.meta.current_page || 1;
    }
  } catch (error) {
    console.error('Failed to fetch FAQs:', error);
  } finally {
    isLoading.value = false;
  }
};

// ============================================================
// 搜索处理
// ============================================================
let searchDebounceTimer = null;
const handleSearch = value => {
  searchValue.value = value;
  clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    fetchFaqs(1);
  }, 500);
};

// ============================================================
// 排序处理
// ============================================================
const handleSortChange = value => {
  sortBy.value = value;
  fetchFaqs(currentPage.value);
};

const handleOrderChange = value => {
  sortOrder.value = value;
  fetchFaqs(currentPage.value);
};

const closeSortMenu = () => {
  isSortMenuOpen.value = false;
};

// ============================================================
// 筛选处理
// ============================================================
const DEFAULT_FILTER = {
  attributeKey: 'question',
  filterOperator: 'contains',
  values: '',
  queryOperator: 'and',
  attributeModel: 'standard',
};

let filterIdCounter = 0;

const handleToggleFilter = () => {
  const shouldOpen = !isFilterOpen.value;
  isFilterOpen.value = shouldOpen;

  if (shouldOpen && filterQuery.value.length === 0) {
    filterQuery.value = [{ ...DEFAULT_FILTER, id: filterIdCounter++ }];
  }
};

const handleCloseFilter = () => {
  isFilterOpen.value = false;
};

const handleApplyFilter = filters => {
  appliedFilters.value = filters;
  isFilterOpen.value = false;
  fetchFaqs(1);
};

const handleClearAllFilters = () => {
  appliedFilters.value = [];
  filterQuery.value = [{ ...DEFAULT_FILTER, id: filterIdCounter++ }];
  fetchFaqs(1);
};

// ============================================================
// 分页处理
// ============================================================
const handlePageChange = page => {
  fetchFaqs(page);
};

// ============================================================
// 批量选择处理
// ============================================================
const buildSelectedCountLabel = computed(() => {
  return t('PILOT.KNOWLEDGE_BASES.SELECT_ALL', {
    count: faqsMeta.value.totalCount,
  });
});

const selectedCountLabel = computed(() => {
  return t('PILOT.KNOWLEDGE_BASES.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleRowHover = (isHovered, id) => {
  hoveredRow.value = isHovered ? id : null;
};

const handleRowSelect = (id, isSelected) => {
  if (isSelected) {
    bulkSelectedIds.value.add(id);
  } else {
    bulkSelectedIds.value.delete(id);
  }
};

const handleSelectAll = () => {
  if (bulkSelectedIds.value.size === faqs.value.length) {
    bulkSelectedIds.value.clear();
  } else {
    faqs.value.forEach(faq => bulkSelectedIds.value.add(faq.id));
  }
};

const handleClearSelection = () => {
  bulkSelectedIds.value.clear();
};

// ============================================================
// 删除处理
// ============================================================
const handleDeleteFaq = faq => {
  selectedFaq.value = faq;
  deleteFaqDialog.value?.dialogRef?.open();
};

const handleDeleteSuccess = () => {
  // 从列表中移除已删除的 FAQ
  if (selectedFaq.value) {
    faqs.value = faqs.value.filter(f => f.id !== selectedFaq.value.id);
    faqsMeta.value.totalCount = Math.max(0, faqsMeta.value.totalCount - 1);
  }
  selectedFaq.value = null;
};

const handleBulkDelete = () => {
  bulkDeleteDialog.value?.open();
};

// 批量删除成功回调
const onBulkDeleteSuccess = () => {
  const idsToDelete = Array.from(bulkSelectedIds.value);
  faqs.value = faqs.value.filter(f => !idsToDelete.includes(f.id));
  faqsMeta.value.totalCount = Math.max(
    0,
    faqsMeta.value.totalCount - idsToDelete.length
  );
  bulkSelectedIds.value = new Set();
};

// 批量操作 ref
const bulkDeleteDialog = ref(null);
const isBulkOperating = ref(false);

// 批量发布
const handleBulkPublish = async () => {
  if (bulkSelectedIds.value.size === 0) return;
  isBulkOperating.value = true;
  try {
    const ids = Array.from(bulkSelectedIds.value);
    const { data } = await PilotFaqsAPI.bulkPublish(ids);
    // 更新列表中的数据
    data.payload.forEach(updatedFaq => {
      updateFaqInList(updatedFaq);
    });
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.BULK_PUBLISH_SUCCESS'));
  } catch (error) {
    console.error('Failed to bulk publish FAQs:', error);
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.BULK_PUBLISH_ERROR'));
  } finally {
    isBulkOperating.value = false;
  }
};

// 批量下架
const handleBulkUnpublish = async () => {
  if (bulkSelectedIds.value.size === 0) return;
  isBulkOperating.value = true;
  try {
    const ids = Array.from(bulkSelectedIds.value);
    const { data } = await PilotFaqsAPI.bulkUnpublish(ids);
    // 更新列表中的数据
    data.payload.forEach(updatedFaq => {
      updateFaqInList(updatedFaq);
    });
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.BULK_UNPUBLISH_SUCCESS'));
  } catch (error) {
    console.error('Failed to bulk unpublish FAQs:', error);
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.BULK_UNPUBLISH_ERROR'));
  } finally {
    isBulkOperating.value = false;
  }
};

// 批量重试
const handleBulkRetry = async () => {
  if (bulkSelectedIds.value.size === 0) return;
  isBulkOperating.value = true;
  try {
    const ids = Array.from(bulkSelectedIds.value);
    const { data } = await PilotFaqsAPI.bulkRetry(ids);
    // 更新列表中的数据
    data.payload.forEach(updatedFaq => {
      updateFaqInList(updatedFaq);
    });
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.BULK_RETRY_SUCCESS'));
  } catch (error) {
    console.error('Failed to bulk retry FAQs:', error);
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.BULK_RETRY_ERROR'));
  } finally {
    isBulkOperating.value = false;
  }
};

// ============================================================
// 编辑处理
// ============================================================
const handleEditFaq = faq => {
  // 检查是否正在向量化
  if (faq.embedding_status === 'embedding_processing') {
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.EDIT.PROCESSING_ERROR'));
    return;
  }

  // 直接进入编辑页
  goToEditPage(faq);
};

const goToEditPage = faq => {
  router.push({
    name: 'pilot_faqs_edit',
    params: {
      knowledgeBaseId: props.knowledgeBase.id,
      faqId: faq.id,
    },
  });
};

// ============================================================
// 发布/下架处理
// ============================================================
const updateFaqInList = updatedFaq => {
  const index = faqs.value.findIndex(f => f.id === updatedFaq.id);
  if (index !== -1) {
    // 使用扩展运算符合并数据，确保响应式更新
    faqs.value[index] = {
      ...faqs.value[index],
      ...updatedFaq,
    };
  }
};

const handlePublishFaq = async faq => {
  try {
    const { data } = await PilotFaqsAPI.publish(faq.id);
    updateFaqInList(data.payload);
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.PUBLISH_SUCCESS'));
  } catch (error) {
    console.error('Failed to publish FAQ:', error);
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.PUBLISH_ERROR'));
  }
};

const handleUnpublishFaq = async faq => {
  try {
    const { data } = await PilotFaqsAPI.unpublish(faq.id);
    updateFaqInList(data.payload);
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.UNPUBLISH_SUCCESS'));
  } catch (error) {
    console.error('Failed to unpublish FAQ:', error);
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.UNPUBLISH_ERROR'));
  }
};

// ============================================================
// 向量化重试处理
// ============================================================
const canRetryFaq = faq => {
  return (
    faq.embedding_status === 'embedding_failed' ||
    faq.embedding_status === 'embedding_completed'
  );
};

const handleRetryFaq = async faq => {
  isRetrying.value[faq.id] = true;
  try {
    const { data } = await PilotFaqsAPI.retry(faq.id);
    updateFaqInList(data.payload);
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.RETRY_SUCCESS'));
  } catch (error) {
    console.error('Failed to retry FAQ:', error);
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.RETRY_ERROR'));
  } finally {
    isRetrying.value[faq.id] = false;
  }
};

// ============================================================
// 状态显示函数
// ============================================================
const getStatusLabel = status => {
  const statusMap = {
    draft: t('PILOT.KNOWLEDGE_BASES.FAQS.STATUS_DRAFT'),
    published: t('PILOT.KNOWLEDGE_BASES.FAQS.STATUS_PUBLISHED'),
  };
  return statusMap[status] || status;
};

const getStatusConfig = status => {
  return STATUS_CONFIG[status] || STATUS_CONFIG.draft;
};

// ============================================================
// 向量化状态显示函数
// ============================================================
const getEmbeddingStatusLabel = embeddingStatus => {
  const statusMap = {
    embedding_pending: t('PILOT.KNOWLEDGE_BASES.FAQS.EMBEDDING_STATUS_PENDING'),
    embedding_processing: t(
      'PILOT.KNOWLEDGE_BASES.FAQS.EMBEDDING_STATUS_PROCESSING'
    ),
    embedding_completed: t(
      'PILOT.KNOWLEDGE_BASES.FAQS.EMBEDDING_STATUS_COMPLETED'
    ),
    embedding_failed: t('PILOT.KNOWLEDGE_BASES.FAQS.EMBEDDING_STATUS_FAILED'),
  };
  return statusMap[embeddingStatus] || embeddingStatus;
};

const getEmbeddingStatusClass = embeddingStatus => {
  const config =
    EMBEDDING_STATUS_CONFIG[embeddingStatus] ||
    EMBEDDING_STATUS_CONFIG.embedding_pending;
  return `${config.bgColor} ${config.textColor}`;
};

const getEmbeddingStatusDotClass = embeddingStatus => {
  const config =
    EMBEDDING_STATUS_CONFIG[embeddingStatus] ||
    EMBEDDING_STATUS_CONFIG.embedding_pending;
  return config.dotColor;
};

const getInitial = name => {
  if (!name) return 'U';
  return name.charAt(0).toUpperCase();
};

const getCreatorColor = name => {
  const colors = [
    '#8B5CF6',
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#EC4899',
    '#6366F1',
  ];
  const index = name?.charCodeAt(0) || 0;
  return colors[index % colors.length];
};

// ============================================================
// 生命周期
// ============================================================
onMounted(() => {
  fetchFaqs();
});
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- 批量操作条 + 搜索工具条 -->
    <div
      v-if="hasFaqs || isSearching || isFiltering"
      class="flex-shrink-0 my-2 relative"
    >
      <BulkSelectBar
        v-model="bulkSelectedIds"
        :all-items="faqs"
        :select-all-label="buildSelectedCountLabel"
        :selected-count-label="selectedCountLabel"
        :delete-label="$t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.BULK_DELETE_BUTTON')"
        class="w-full"
      >
        <template #actions>
          <div class="flex items-center gap-2">
            <Button
              :label="$t('PILOT.KNOWLEDGE_BASES.FAQS.BULK_PUBLISH')"
              icon="i-lucide-upload-cloud"
              size="sm"
              ghost
              blue
              :disabled="isBulkOperating"
              @click="handleBulkPublish"
            />
            <Button
              :label="$t('PILOT.KNOWLEDGE_BASES.FAQS.BULK_UNPUBLISH')"
              icon="i-lucide-download-cloud"
              size="sm"
              ghost
              blue
              :disabled="isBulkOperating"
              @click="handleBulkUnpublish"
            />
            <Button
              :label="$t('PILOT.KNOWLEDGE_BASES.FAQS.BULK_RETRY')"
              icon="i-lucide-refresh-cw"
              size="sm"
              ghost
              blue
              :disabled="isBulkOperating"
              @click="handleBulkRetry"
            />
            <Button
              :label="$t('PILOT.KNOWLEDGE_BASES.FAQS.BULK_DELETE_BUTTON')"
              icon="i-lucide-trash"
              size="sm"
              ghost
              ruby
              :disabled="isBulkOperating"
              @click="handleBulkDelete"
            />
          </div>
        </template>
        <template #default-actions>
          <div class="flex items-center justify-between w-full">
            <!-- 左侧：搜索 + 筛选 + 排序 -->
            <div class="flex flex-wrap items-center gap-3">
              <!-- 搜索框 -->
              <div class="flex-1 min-w-[200px] max-w-md">
                <Input
                  :model-value="searchValue"
                  type="search"
                  :placeholder="
                    t('PILOT.KNOWLEDGE_BASES.FAQS.SEARCH_PLACEHOLDER')
                  "
                  :custom-input-class="[
                    'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
                  ]"
                  class="w-full"
                  @update:model-value="handleSearch"
                >
                  <template #prefix>
                    <Icon
                      icon="i-lucide-search"
                      class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
                    />
                  </template>
                </Input>
              </div>

              <!-- 筛选和排序按钮 -->
              <div class="flex gap-2">
                <!-- 筛选按钮 -->
                <Button
                  id="toggleKnowledgeBaseFaqsFilterButton"
                  icon="i-lucide-sliders-horizontal"
                  color="slate"
                  size="sm"
                  variant="ghost"
                  :class="{ 'bg-n-alpha-2': isFilterOpen }"
                  @click="handleToggleFilter"
                />

                <!-- 排序按钮 -->
                <div class="relative">
                  <Button
                    icon="i-lucide-arrow-down-up"
                    color="slate"
                    size="sm"
                    variant="ghost"
                    :class="isSortMenuOpen ? 'bg-n-alpha-2' : ''"
                    @click="isSortMenuOpen = !isSortMenuOpen"
                  />

                  <!-- 排序弹窗 -->
                  <div
                    v-if="isSortMenuOpen"
                    v-on-clickaway="closeSortMenu"
                    class="absolute top-full mt-1 ltr:-right-32 rtl:-left-32 sm:ltr:right-0 sm:rtl:left-0 flex flex-col gap-4 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak w-72 rounded-xl p-4 z-20"
                  >
                    <!-- 排序字段 -->
                    <div class="flex items-center justify-between gap-2">
                      <span class="text-sm text-n-slate-12">
                        {{ t('PILOT.KNOWLEDGE_BASES.SORT.SORT_BY_LABEL') }}
                      </span>
                      <SelectMenu
                        :model-value="sortBy"
                        :options="sortMenus"
                        :label="activeSortLabel"
                        @update:model-value="handleSortChange"
                      />
                    </div>

                    <!-- 排序顺序 -->
                    <div class="flex items-center justify-between gap-2">
                      <span class="text-sm text-n-slate-12">
                        {{ t('PILOT.KNOWLEDGE_BASES.SORT.ORDER_LABEL') }}
                      </span>
                      <SelectMenu
                        :model-value="sortOrder"
                        :options="orderingMenus"
                        :label="activeOrderingLabel"
                        @update:model-value="handleOrderChange"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </BulkSelectBar>

      <!-- 筛选弹窗 -->
      <div v-if="isFilterOpen" class="absolute top-full left-0 z-50 mt-2">
        <KnowledgeBaseFaqsFilter
          v-model="filterQuery"
          @close="handleCloseFilter"
          @apply-filter="handleApplyFilter"
          @clear-filters="handleClearAllFilters"
        />
      </div>
    </div>

    <!-- 空状态 -->
    <div v-if="showEmptyState && !isLoading" class="flex flex-col gap-6">
      <!-- 虚线图文介绍区域 -->
      <section class="custom-dashed-border rounded-2xl py-5 px-6">
        <div
          class="flex flex-col md:flex-row items-start md:items-center gap-6"
        >
          <div
            class="flex-shrink-0 w-[7.5rem] h-[6.5rem] rounded-lg flex items-center justify-center overflow-hidden"
          >
            <img
              :src="faqThumbnail"
              :alt="t('PILOT.KNOWLEDGE_BASES.FAQS.ADD.GUIDE_TITLE')"
              class="w-full h-full object-cover rounded-lg block dark:hidden"
            />
            <img
              :src="faqThumbnailDark"
              :alt="t('PILOT.KNOWLEDGE_BASES.FAQS.ADD.GUIDE_TITLE')"
              class="w-full h-full object-cover rounded-lg hidden dark:block"
            />
          </div>
          <div class="flex flex-col flex-1 gap-3">
            <p class="text-n-slate-12 text-sm font-medium">
              {{ t('PILOT.KNOWLEDGE_BASES.FAQS.ADD.GUIDE_TITLE') }}
            </p>
            <p class="text-n-slate-11 text-sm">
              {{ t('PILOT.KNOWLEDGE_BASES.FAQS.ADD.GUIDE_DESCRIPTION') }}
            </p>
          </div>
        </div>
      </section>
      <EmptyStateLayout
        :title="t('PILOT.KNOWLEDGE_BASES.FAQS.EMPTY_TITLE')"
        :subtitle="t('PILOT.KNOWLEDGE_BASES.FAQS.EMPTY_DESCRIPTION')"
        :action-perms="['administrator']"
      >
        <template #empty-state-item>
          <div class="w-full space-y-3">
            <!-- 示例 FAQ 列表 -->
            <div
              v-for="(faq, index) in exampleFaqs.slice(0, 3)"
              :key="`example-${index}`"
              class="flex items-start gap-4 p-4 bg-n-slate-white dark:bg-n-slate-800 rounded-lg border border-n-weak"
            >
              <!-- 左侧：问答图标 -->
              <div
                class="h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-n-brand text-white"
              >
                <Icon icon="i-lucide-message-square" class="h-5 w-5" />
              </div>

              <!-- 右侧：问答内容 -->
              <div class="flex-1 min-w-0 space-y-2">
                <!-- 问题 -->
                <div class="flex items-center gap-2">
                  <span class="text-xs font-medium text-n-slate-500">Q:</span>
                  <p
                    class="text-sm font-medium text-n-slate-900 dark:text-n-slate-100 line-clamp-2"
                  >
                    {{ faq.question }}
                  </p>
                </div>

                <!-- 答案 -->
                <div class="flex items-start gap-2">
                  <span class="text-xs font-medium text-n-slate-500">A:</span>
                  <p
                    class="text-sm text-n-slate-700 dark:text-n-slate-300 line-clamp-2"
                  >
                    {{ faq.answer }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template #actions>
          <Button
            :label="t('PILOT.KNOWLEDGE_BASES.FAQS.CREATE_BUTTON')"
            icon="i-lucide-plus"
            blue
            @click="$emit('create')"
          />
        </template>
      </EmptyStateLayout>
    </div>

    <!-- 无搜索结果 -->
    <div
      v-else-if="showNoResults && !isLoading"
      class="flex flex-col items-center justify-center py-20"
    >
      <div class="i-lucide-search-x h-16 w-16 text-n-slate-5 mb-4" />
      <p class="text-n-slate-11 text-lg font-medium">
        {{ t('PILOT.KNOWLEDGE_BASES.FAQS.NO_RESULTS') }}
      </p>
      <p class="text-n-slate-10 text-sm mt-2">
        {{ t('PILOT.KNOWLEDGE_BASES.FAQS.TRY_DIFFERENT') }}
      </p>
    </div>

    <!-- FAQ 列表 -->
    <div v-else-if="hasFaqs" class="flex-1 overflow-hidden flex flex-col">
      <!-- 表格 Loading -->
      <div
        v-if="isLoading"
        class="flex items-center justify-center py-20 flex-1"
      >
        <Spinner />
      </div>

      <!-- 表格 + 分页 -->
      <template v-else>
        <!-- 表格区域 -->
        <div class="flex-1 overflow-auto">
          <table class="min-w-[1800px] divide-y divide-n-weak">
            <thead>
              <tr>
                <th class="py-3 pl-4 w-[40px]" />
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[400px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.FAQS.TABLE_QUESTION') }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[400px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.FAQS.TABLE_SIMILAR_QUESTIONS') }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[500px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.FAQS.TABLE_ANSWERS') }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[120px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.FAQS.TABLE_STATUS') }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[130px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.FAQS.TABLE_EMBEDDING_STATUS') }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[120px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.FAQS.TABLE_CREATOR') }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[220px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.FAQS.TABLE_CREATED_AT') }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[120px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.FAQS.TABLE_UPDATER') }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[220px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.FAQS.TABLE_UPDATED_AT') }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-right text-n-slate-11 whitespace-nowrap w-[100px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.FAQS.TABLE_ACTIONS') }}
                </th>
              </tr>
            </thead>

            <tbody class="divide-y divide-n-weak text-n-slate-11">
              <tr
                v-for="faq in faqsWithStyles"
                :key="faq.id"
                class="hover:bg-n-alpha-1 transition-colors duration-200"
                @mouseenter="handleRowHover(true, faq.id)"
                @mouseleave="handleRowHover(false, faq.id)"
              >
                <!-- Checkbox -->
                <td class="py-4 pl-4 align-top">
                  <div
                    v-show="hoveredRow === faq.id || bulkSelectedIds.size > 0"
                    class="scale-125 origin-left"
                  >
                    <Checkbox
                      :model-value="bulkSelectedIds.has(faq.id)"
                      @update:model-value="
                        isSelected => handleRowSelect(faq.id, isSelected)
                      "
                      @click.stop
                    />
                  </div>
                </td>

                <!-- 标准问题 -->
                <td
                  class="py-4 px-4 align-top cursor-pointer"
                  @click="handleEditFaq(faq)"
                >
                  <div class="flex items-start gap-2">
                    <div
                      class="h-6 w-6 flex-shrink-0 flex items-center justify-center rounded bg-n-brand text-white mt-0.5"
                    >
                      <Icon
                        icon="i-lucide-message-circle-question"
                        class="h-3.5 w-3.5"
                      />
                    </div>
                    <p
                      class="text-sm font-medium text-n-slate-900 dark:text-n-slate-100 hover:text-n-brand transition-colors"
                    >
                      {{ faq.question }}
                    </p>
                  </div>
                </td>

                <!-- 相似问题（多行） -->
                <td class="py-4 px-4 align-top">
                  <div
                    v-if="faq.similar_questions?.length > 0"
                    class="space-y-1"
                  >
                    <div
                      v-for="(sq, index) in faq.similar_questions"
                      :key="sq.id"
                      class="text-sm text-n-slate-700 dark:text-n-slate-300"
                    >
                      <span class="text-n-slate-10 mr-1">{{ index + 1 }}.</span>
                      {{ sq.question }}
                    </div>
                  </div>
                  <span v-else class="text-sm text-n-slate-10">-</span>
                </td>

                <!-- 答案（多行） -->
                <td class="py-4 px-4 align-top">
                  <div v-if="faq.answers?.length > 0" class="space-y-2">
                    <div
                      v-for="(answer, index) in faq.answers"
                      :key="answer.id"
                      class="text-sm text-n-slate-700 dark:text-n-slate-300"
                    >
                      <span class="text-n-slate-10 mr-1">{{ index + 1 }}.</span>
                      {{ answer.answer }}
                    </div>
                  </div>
                  <span v-else class="text-sm text-n-slate-10">-</span>
                </td>

                <!-- 状态 -->
                <td class="py-4 px-4 align-top">
                  <div
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    :class="faq.statusClass"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      :class="[faq.statusDotClass]"
                    />
                    <span>{{ getStatusLabel(faq.status) }}</span>
                  </div>
                </td>

                <!-- 向量化状态 -->
                <td class="py-4 px-4 align-top">
                  <div
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    :class="faq.embeddingStatusClass"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      :class="[faq.embeddingStatusDotClass]"
                    />
                    <span>{{
                      getEmbeddingStatusLabel(faq.embedding_status)
                    }}</span>
                  </div>
                </td>

                <!-- 创建人 -->
                <td class="py-4 px-4 align-top">
                  <div class="flex items-center gap-2">
                    <div
                      class="w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0"
                      :style="{
                        backgroundColor: getCreatorColor(faq.creator?.name),
                      }"
                    >
                      {{ getInitial(faq.creator?.name) }}
                    </div>
                    <span class="text-sm text-n-slate-11">
                      {{ faq.creator?.name || '-' }}
                    </span>
                  </div>
                </td>

                <!-- 创建时间 -->
                <td class="py-4 px-4 align-top">
                  <span class="text-sm text-n-slate-10">
                    {{ faq.created_at ? dynamicTime(faq.created_at) : '-' }}
                  </span>
                </td>

                <!-- 修改人 -->
                <td class="py-4 px-4 align-top">
                  <div class="flex items-center gap-2">
                    <div
                      v-if="faq.updater?.name"
                      class="w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0"
                      :style="{
                        backgroundColor: getCreatorColor(faq.updater?.name),
                      }"
                    >
                      {{ getInitial(faq.updater?.name) }}
                    </div>
                    <span class="text-sm text-n-slate-11">
                      {{ faq.updater?.name || '-' }}
                    </span>
                  </div>
                </td>

                <!-- 修改时间 -->
                <td class="py-4 px-4 align-top">
                  <span class="text-sm text-n-slate-10">
                    {{ faq.updated_at ? dynamicTime(faq.updated_at) : '-' }}
                  </span>
                </td>

                <!-- 操作 -->
                <td class="py-4 px-4 align-top text-right">
                  <div class="flex items-center justify-end gap-1">
                    <!-- 发布按钮（草稿状态显示） -->
                    <Button
                      v-if="
                        faq.status === 'draft' &&
                        faq.embedding_status !== 'embedding_processing'
                      "
                      v-tooltip.top="t('PILOT.KNOWLEDGE_BASES.FAQS.PUBLISH')"
                      icon="i-lucide-upload-cloud"
                      xs
                      slate
                      faded
                      @click="handlePublishFaq(faq)"
                    />
                    <!-- 下架按钮（已发布状态显示） -->
                    <Button
                      v-if="faq.status === 'published'"
                      v-tooltip.top="t('PILOT.KNOWLEDGE_BASES.FAQS.UNPUBLISH')"
                      icon="i-lucide-download-cloud"
                      xs
                      slate
                      faded
                      @click="handleUnpublishFaq(faq)"
                    />
                    <Button
                      v-if="faq.embedding_status !== 'embedding_processing'"
                      v-tooltip.top="t('PILOT.KNOWLEDGE_BASES.OPTIONS.EDIT')"
                      icon="i-lucide-pencil"
                      xs
                      slate
                      faded
                      @click="handleEditFaq(faq)"
                    />
                    <!-- 重试按钮（失败或已完成状态显示） -->
                    <Button
                      v-if="canRetryFaq(faq)"
                      v-tooltip.top="t('PILOT.KNOWLEDGE_BASES.FAQS.RETRY')"
                      icon="i-lucide-refresh-cw"
                      xs
                      slate
                      faded
                      :is-loading="isRetrying[faq.id]"
                      @click="handleRetryFaq(faq)"
                    />
                    <Button
                      v-tooltip.top="t('PILOT.KNOWLEDGE_BASES.OPTIONS.DELETE')"
                      icon="i-lucide-trash-2"
                      xs
                      ruby
                      faded
                      @click="handleDeleteFaq(faq)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分页 -->
        <footer
          v-if="faqsMeta.totalCount > 0"
          class="flex-shrink-0 sticky bottom-0 z-10 px-4 pb-4 mt-2 bg-n-slate-white dark:bg-n-slate-800 border-t border-n-weak"
        >
          <PaginationFooter
            :current-page="faqsMeta.currentPage"
            :total-items="faqsMeta.totalCount"
            :items-per-page="faqsMeta.perPage"
            @update:current-page="handlePageChange"
          />
        </footer>
      </template>
    </div>

    <!-- 删除对话框 -->
    <DeleteDialog
      ref="deleteFaqDialog"
      :entity="{ id: selectedFaq?.id, name: selectedFaq?.question }"
      type="Faq"
      translation-key="KNOWLEDGE_BASES.FAQS"
      @delete-success="handleDeleteSuccess"
    />

    <!-- 批量删除对话框 -->
    <BulkDeleteDialog
      ref="bulkDeleteDialog"
      :bulk-ids="bulkSelectedIds"
      type="Faq"
      translation-key="KNOWLEDGE_BASES.FAQS"
      @delete-success="onBulkDeleteSuccess"
    />
  </div>
</template>
